import { ConversationModel } from 'Models/ConversationModel';

export type ConversationsFilterType = 'all' | 'unread' | 'group' | 'archived';

export const CONVERSATIONS_FILTER_OPTIONS: ConversationsFilterType[] = ['all', 'unread', 'group', 'archived'];

export type ConversationsListSliceState = {
  conversationsList: ConversationModel[];
  isLoadingConversationsList: boolean;
  conversationsListFilter: ConversationsFilterType;
  lastConversationId?: string;
  hasMoreConversations?: boolean;
  totalConversations?: number;
  hasAttemptedConversationsFetch?: boolean;
};

export type ConversationsListSlice = {
  /**
   * Adds conversations to the end of the list.
   * Warning: To be used solely for infinite scroll loading more behavior.
   * @param conversations - The conversations to append
   * @param hasMoreConversations - Whether there are more conversations to load
   * @param pinnedConversations - The pinned conversations to append
   */
  appendConversationsList: (
    conversations: ConversationModel[],
    hasMoreConversations: boolean,
    pinnedConversations?: ConversationModel[]
  ) => void;
  /**
   * Add a conversation to the list.
   * If conversation already exists, it will be updated.
   * @param conversation - The conversation to add
   */
  addConversationToList: (conversations: ConversationModel) => void;
  /**
   * Replaces existing conversations list with the new one.
   * @param conversations - The conversations to set
   */
  setConversationsList: (conversations: ConversationModel[]) => void;
  /**
   * Replaces existing conversation on the list with the new one.
   * @param conversation - The conversation to update
   */
  updateConversation: (conversation: ConversationModel) => void;
  /**
   * Updates specific fields of a conversation in the list.
   * @param conversationId - The id of the conversation to update
   * @param conversation - The fields to update
   */
  updateConversationFields: (
    conversationId: string,
    conversation: Partial<ConversationModel>
  ) => void;
  /**
   * Sets the loading status of the conversations list.
   * @param isLoading - The new loading status
   */
  setIsLoadingConversationsList: (isLoading: boolean) => void;
  /**
   * Marks that at least one conversations fetch attempt (success or failure) happened.
   */
  setHasAttemptedConversationsFetch: (hasAttempted: boolean) => void;
  /**
   * Sets the filter of the conversations list and resets the list to the first page.
   * @param conversationsListFilter - The new filter
   */
  setConversationsListFilter: (
    conversationsListFilter: ConversationsFilterType
  ) => void;
  /**
   * Removes a conversation from the list.
   * @param conversationId - The id of the conversation to remove
   */
  removeConversationFromList: (conversationId: string) => void;

  /**
   * Merges conversations from polling with the existing list.
   * @param conversations - The conversations from polling
   * @param pinnedConversations - The pinned conversations from polling
   */
  mergeConversationsListFromPolling: (
    conversations: ConversationModel[],
    pinnedConversations?: ConversationModel[]
  ) => void;
} & ConversationsListSliceState;
